import { LoadingOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { Select, SelectProps, Space, Tag } from 'antd';
import { useAuthorization } from 'components/Authorization';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import { sortBy } from 'lodash-es';
import dayjs from 'lib/dayjs';
import { useContext, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { latestDate } from 'services/api/dashboard';
import DepartmentSelect, {
  DepartmentsSelect,
} from 'views/Dashboard/ImplementData/components/Filter/DepartmentSelect';
import RangePicker from 'views/Dashboard/ImplementData/components/Filter/ExtraNode';
import { DashboardCurrencyCtx } from 'views/Dashboard/ImplementData/hooks/useCurrency';
import { getUpdateDate } from 'views/Dashboard/ImplementData/utils';
import { FilterCtxType, useFilterCtx } from './Filter';
import PerfTable from './PerfTable';
import PerfTableRealTime from './PerfTableRealTime';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';

export function useCustomSelect(
  props: {
    department?: Dashboard.Department;
    departments?: Dashboard.Department[];
  } & SelectProps,
) {
  const { department, departments, ...otherProps } = props;

  const { t } = useTranslate();

  const { payload: { id } = {} } = useAuthorization<any>();

  const groupPermission = useGetPermission()('绩效报告-查看部门绩效');

  const departmentList = departments || (department ? [department] : []);

  const { data: listRes, isLoading: loading } = useQuery({
    ...queries.accounts.assistantAccounts({
      page: 1,
      pageSize: 1000,
      departmentIds: departmentList.map((_) => _.id),
      includeSupervisor: true,
    }),
    enabled: departmentList.length > 0,
  });

  const list = useMemo(() => listRes?.data || [], [listRes?.data]);

  const { options, adminId } = useMemo(() => {
    const adminId = list.find((x) => x.roles.some((role) => role.name === 'SUPER_ADMIN'))?.id;

    const options = sortBy(list, (v) => {
      if (v.status === 'DISABLE') {
        return 2;
      }
      if (v.sleepFlag) {
        return 1;
      }
      return 0;
    })
      .filter((x) => {
        if (!groupPermission && `${x.id}` !== `${id}`) {
          return false;
        }

        return x.roles.every((role) => role.name !== 'SUPER_ADMIN');
      })
      .map((x) => {
        const tag = () => {
          if (x.status === 'DISABLE') {
            return <Tag>{t('ViewDashboard.客服状态.已停用')}</Tag>;
          }
          if (x.sleepFlag) {
            return <Tag color="orange">{t('ViewDashboard.客服状态.已休眠')}</Tag>;
          }
          return null;
        };
        return {
          label: (
            <Space size={6}>
              {x.name}
              {tag()}
            </Space>
          ),
          value: x.id,
          filterKey: x.name,
        };
      });
    return {
      options,
      adminId,
    };
  }, [list, groupPermission, id, t]);

  const customSelect = (
    <Select
      style={{ minWidth: '150px' }}
      loading={loading}
      options={options}
      placeholder={t('ViewDashboard.请选择客服')}
      maxTagCount={2}
      showArrow
      allowClear
      optionFilterProp="filterKey"
      dropdownMatchSelectWidth={false}
      mode="multiple"
      {...otherProps}
    />
  );

  return {
    t,
    options,
    adminId,
    customSelect,
  };
}

function AssistantPerfContent() {
  const { value, onChange } = useFilterCtx();

  const { departments } = value;

  const { adminId, customSelect } = useCustomSelect({
    departments,
    value: value.assistantIds,
    onChange: (value) => {
      onChange((pre) => ({ ...pre, assistantIds: value }));
    },
  });

  const departmentSelect = (
    <DepartmentsSelect
      dashboardType="performance"
      value={departments}
      onChange={(departments) => {
        onChange((pre) => ({
          ...pre,
          departments,
          assistantIds: undefined,
        }));
      }}
    />
  );

  const [maxLoading, setMaxLoading] = useState(true);

  const [max, setMax] = useState(() => getUpdateDate()[0]);

  const { setDataUpdateTime } = useContext(DashboardCurrencyCtx);

  useMount(() => {
    setMaxLoading(true);

    const dashboardName = 'PerformanceAgent' as const;

    latestDate({ dashboardName })
      .then((res) => {
        const target = res.find((_) => _.dashboardName === dashboardName);

        if (target) {
          const endDate = dayjs(target.latestDate).subtract(1, 'days');

          onChange((pre) => {
            return {
              ...pre,
              dateRange: [pre.dateRange[0], endDate],
            };
          });

          setMax(endDate);

          setDataUpdateTime?.(target.latestDate);
        } else {
          setDataUpdateTime?.(undefined);
        }
      })
      .catch(() => {})
      .finally(() => {
        setMaxLoading(false);
      });
  });

  if (maxLoading) {
    return <LoadingOutlined />;
  }

  return (
    <PerfTable
      adminId={adminId}
      rangeSelect={<RangePicker<FilterCtxType> value={value} onChange={onChange} max={max} />}
      customSelect={customSelect}
      departmentSelect={departmentSelect}
    />
  );
}

function AssistantPerfRealTime() {
  const { value, onChange } = useFilterCtx();

  const { department } = value;

  const { options, adminId, customSelect } = useCustomSelect({
    department,
    value: value.assistantIds,
    onChange: (value) => {
      onChange((pre) => ({ ...pre, assistantIds: value }));
    },
  });

  const departmentSelect = (
    <DepartmentSelect
      dashboardType="performance"
      value={department}
      onChange={(department) => {
        onChange((pre) => ({
          ...pre,
          department,
          assistantIds: undefined,
        }));
      }}
    />
  );

  return (
    <PerfTableRealTime
      adminId={adminId}
      customSelect={customSelect}
      departmentSelect={departmentSelect}
      customerOptions={options}
    />
  );
}

function Content({ selType }: { selType: string }) {
  return selType === 'realTime' ? <AssistantPerfRealTime /> : <AssistantPerfContent />;
}

export default Content;
